
import { useState } from "react";

import { FilledButton, OutlinedButton } from "../../common/Button";


import useCurrentUser from "../../../hooks/useCurrentUser";
import { FreelancerRole, FreelancerSkillExperience, FreelancerToolExperience, IFreelancer } from "../../../data_models/freelancer";
import Lightbox from "../../common/Lightbox";
import TextInputField from "../../common/TextInputField";
import TextInputArea from "../../common/TextInputArea";
import { OutlinedDropdownMenu } from "../../common/DropdownMenu";
import MultiTextSelect from "../../common/MultiTextSelect";
import { OutlinedMultiDropdownMenu } from "../../common/MultiDropdownMenu";
import { getNumericEnumValues } from "../../../utils/enumUtils";
import { UserEnumTextMapper } from "../../../data_models/enumToTextMapper";
import EditSkillExperience from "../../profile/common/EditSkillExperience";
import RichTextEditor from "../../common/RichTextEditor";


interface EditProfessionalInfoFreelancerProps {
    onClose: () => void;
}

export const EditProfessionalInfoFreelancer: React.FC<EditProfessionalInfoFreelancerProps> = ({ onClose }) => {

    const { currentUser, updateCurrentUser } = useCurrentUser();
    const [loading, setLoading] = useState(false);

    const [bio, setBio] = useState((currentUser as IFreelancer)?.bio || '');
    const [currentOccupation, setCurrentOccupation] = useState((currentUser as IFreelancer)?.currentOccupation || '');
    const [roles, setRoles] = useState<FreelancerRole[]>((currentUser as IFreelancer)?.roles || []);
    const [industries, setIndustries] = useState<string[]>((currentUser as IFreelancer)?.industries || []);
    const [skills, setSkills] = useState<FreelancerSkillExperience[]>((currentUser as IFreelancer)?.skills || []);
    const [tools, setTools] = useState<FreelancerToolExperience[]>((currentUser as IFreelancer)?.tools || []);


    const onSubmit = async () => {
        setLoading(true);
        try {

            const updateObj: IFreelancer = {
                bio: bio,
                currentOccupation: currentOccupation,
                roles: roles,
                industries: industries,
                skills: skills.filter(skill => skill.skill !== ''),
                tools: tools.filter(tool => tool.tool !== ''),
            }

            await updateCurrentUser(updateObj);
        } catch (error: any) {
            console.error(error);
        } finally {
            setLoading(false);
            onClose();
        }
    }

    return (
        <Lightbox
            content={
                <div className="flex flex-col space-y-6 w-full" >
                    <h4>Update Professional Information</h4>

                    {/* Bio */}
                    <RichTextEditor
                        label="Bio"
                        value={bio}
                        onChange={(content) => setBio(content)}
                        placeholder="Let us know a little about yourself"
                    />

                    {/* Current Occupation */}
                    <TextInputField
                        type="text"
                        id="currentOccupation"
                        label="Current Occupation"
                        value={currentOccupation}
                        onChange={(e) => setCurrentOccupation(e.target.value)}
                        placeholder="Enter your current occupation"
                    />

                    {/* Roles */}
                    <OutlinedMultiDropdownMenu
                        options={getNumericEnumValues(FreelancerRole)}
                        optionsToTextMapper={UserEnumTextMapper.freelancerRole}
                        elements={roles}
                        setElements={setRoles}
                        label="Roles (Select roles you can imagine to work as a freelancer)"

                        maxNumSelections={3}
                    />

                    {/* Industries */}
                    <MultiTextSelect
                        elements={industries}
                        setElements={setIndustries}
                        label="Industries in which you have knowledge"
                    />

                    {/* Skills */}
                    <EditSkillExperience
                        label="Your Skills and Experiences"
                        type="skills"
                        data={skills}
                        setData={(newSkills) => setSkills(newSkills as FreelancerSkillExperience[])}
                    />

                    {/* Tools */}
                    <EditSkillExperience
                        label="Tools you know"
                        type="tools"
                        data={tools}
                        setData={(newTools) => setTools(newTools as FreelancerToolExperience[])}
                    />

                    <div className="flex flex-row justify-end w-full space-x-2">
                        <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
                        <FilledButton
                            isLoading={loading}
                            onClick={onSubmit}>Submit</FilledButton>
                    </div>
                </div>}
            onClose={onClose}
        />
    );
}

